import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  MenuItem
} from '@mui/material';
import { 
  Save, 
  Image as ImageIcon,
  Category as CategoryIcon,
  LocalOffer,
  Add,
  Edit,
  Delete,
  Numbers,
  RestoreFromTrash,
  ShoppingCart,
  Search,
  People,
  CloudUpload,
  DeleteOutline,
  Backup
} from '@mui/icons-material';
import AdminPageHeader from '../components/AdminPageHeader';
import uploadService from '../services/uploadService';
import categoryService from '../services/categoryService';
import brandService from '../services/brandService';
import productService from '../services/productService';
import userService from '../services/userService';
import backupService from '../services/backupService';
import SaleNumberingConfig from '../components/SaleNumberingConfig';
import { useBusiness } from '../context/BusinessContext';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings = () => {
  const { businessInfo, updateBusinessInfo } = useBusiness();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backupInfo, setBackupInfo] = useState({
    databaseSize: '-',
    backupsCount: 0,
    backupsTotalSize: '-'
  });
  
  // Business Info State
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [formData, setFormData] = useState({
    businessName: '',
    cuit: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    instagram: '',
    facebook: '',
    whatsapp: '',
    openingHours: '',
    taxCondition: '',
    province: '',
    city: '',
    postalCode: ''
  });

  // Categories State
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });

  // Brands State
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandFormData, setBrandFormData] = useState({
    name: '',
    description: ''
  });

  // Deleted Products State
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [loadingDeletedProducts, setLoadingDeletedProducts] = useState(false);
  const [searchDeleted, setSearchDeleted] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({
    open: false,
    product: null,
    conflictData: null,
    selectedCode: ''
  });

  // Users State
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phone: '',
    role: 'vendedor',
    isActive: true
  });

  useEffect(() => {
    if (businessInfo) {
      setFormData({
        businessName: businessInfo.businessName || '',
        cuit: businessInfo.cuit || '',
        address: businessInfo.address || '',
        phone: businessInfo.phone || '',
        email: businessInfo.email || '',
        website: businessInfo.website || '',
        description: businessInfo.description || '',
        instagram: businessInfo.instagram || '',
        facebook: businessInfo.facebook || '',
        whatsapp: businessInfo.whatsapp || '',
        openingHours: businessInfo.openingHours || '',
        taxCondition: businessInfo.taxCondition || '',
        province: businessInfo.province || '',
        city: businessInfo.city || '',
        postalCode: businessInfo.postalCode || ''
      });
      // El logo siempre está en /logo.png
      setLogoPreview(businessInfo.logo ? businessInfo.logo : '');
    }
  }, [businessInfo]);

  useEffect(() => {
    if (currentTab === 0) {
      loadBackupInfo();
    } else if (currentTab === 1) {
      loadCategories();
    } else if (currentTab === 2) {
      loadBrands();
    } else if (currentTab === 3) {
      loadUsers();
    } else if (currentTab === 5) {
      loadDeletedProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  useEffect(() => {
    if (currentTab === 5) {
      loadDeletedProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDeleted]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setMessage({ type: '', text: '' });
  };

  // Business Info Functions
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Estás seguro de eliminar el logo actual?')) {
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        logo: null
      };

      const result = await updateBusinessInfo(data);
      
      if (result.success) {
        setLogoPreview('');
        setLogoFile(null);
        setMessage({ type: 'success', text: 'Logo eliminado correctamente' });
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al eliminar logo' });
    }
    setLoading(false);
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let logoUrl = businessInfo?.logo || '/logo.png';

      if (logoFile) {
        const uploadResult = await uploadService.uploadLogo(logoFile);
        if (uploadResult.success) {
          logoUrl = uploadResult.data.url; // Siempre será "/logo.png"
          setMessage({ type: 'success', text: 'Logo actualizado correctamente' });
        } else {
          setMessage({ type: 'error', text: uploadResult.message });
          setLoading(false);
          return;
        }
      }

      const data = {
        ...formData,
        logo: logoUrl
      };

      const result = await updateBusinessInfo(data);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Información actualizada correctamente' });
        setLogoFile(null);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar información' });
    }

    setLoading(false);
  };

  // Categories Functions
  const loadCategories = async () => {
    setLoadingCategories(true);
    const result = await categoryService.getAll();
    if (result.success) {
      setCategories(result.data);
    }
    setLoadingCategories(false);
  };

  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        description: category.description || ''
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        description: ''
      });
    }
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setEditingCategory(null);
  };

  const handleCategorySubmit = async () => {
    let result;
    if (editingCategory) {
      result = await categoryService.update(editingCategory.id, categoryFormData);
    } else {
      result = await categoryService.create(categoryFormData);
    }

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      handleCloseCategoryDialog();
      loadCategories();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría?')) {
      const result = await categoryService.delete(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadCategories();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  // Brands Functions
  const loadBrands = async () => {
    setLoadingBrands(true);
    const result = await brandService.getAll();
    if (result.success) {
      setBrands(result.data);
    }
    setLoadingBrands(false);
  };

  const handleOpenBrandDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setBrandFormData({
        name: brand.name,
        description: brand.description || ''
      });
    } else {
      setEditingBrand(null);
      setBrandFormData({
        name: '',
        description: ''
      });
    }
    setOpenBrandDialog(true);
  };

  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setEditingBrand(null);
  };

  const handleBrandSubmit = async () => {
    let result;
    if (editingBrand) {
      result = await brandService.update(editingBrand.id, brandFormData);
    } else {
      result = await brandService.create(brandFormData);
    }

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      handleCloseBrandDialog();
      loadBrands();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleDeleteBrand = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta marca?')) {
      const result = await brandService.delete(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadBrands();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  // Backup Functions
  const loadBackupInfo = async () => {
    const result = await backupService.getInfo();

    if (result.success) {
      setBackupInfo({
        databaseSize: result.data?.database?.size || '-',
        backupsCount: result.data?.backups?.count || 0,
        backupsTotalSize: result.data?.backups?.totalSize || '-'
      });
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);

    const result = await backupService.createDownloadAndDelete();

    if (result.success) {
      setMessage({
        type: result.warning ? 'warning' : 'success',
        text: result.message || 'Backup descargado correctamente'
      });
      await loadBackupInfo();
    } else {
      setMessage({
        type: 'error',
        text: result.message || 'No se pudo crear el backup'
      });
    }

    setCreatingBackup(false);
  };

  // Optimized handler for business info form
  const handleBusinessFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Optimized handlers for category form
  const handleCategoryFormChange = (field, value) => {
    setCategoryFormData(prev => ({ ...prev, [field]: value }));
  };

  // Optimized handlers for brand form
  const handleBrandFormChange = (field, value) => {
    setBrandFormData(prev => ({ ...prev, [field]: value }));
  };

  // Deleted Products Functions
  const loadDeletedProducts = async () => {
    setLoadingDeletedProducts(true);
    const params = {
      search: searchDeleted || undefined,
      limit: 100
    };

    const result = await productService.getDeleted(params);
    
    if (result.success) {
      setDeletedProducts(result.data);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    
    setLoadingDeletedProducts(false);
  };

  const handleRestore = async (product) => {
    const result = await productService.restore(product.id);

    if (result.success) {
      setMessage({ 
        type: 'success', 
        text: `✅ Producto "${product.name}" restaurado correctamente` 
      });
      loadDeletedProducts();
    } else if (result.error === 'CODIGO_EN_PRODUCTO_ACTIVO') {
      setRestoreDialog({
        open: true,
        product: product,
        conflictData: result.data,
        selectedCode: result.data.sugerencias[0] || ''
      });
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleRestoreWithNewCode = async () => {
    const { product, selectedCode } = restoreDialog;
    
    if (!selectedCode || selectedCode.trim() === '') {
      setMessage({ type: 'error', text: 'Debes seleccionar o ingresar un código' });
      return;
    }

    const result = await productService.restore(product.id, selectedCode);

    if (result.success) {
      setMessage({ 
        type: 'success', 
        text: `✅ Producto "${product.name}" restaurado con código "${selectedCode}"` 
      });
      setRestoreDialog({ open: false, product: null, conflictData: null, selectedCode: '' });
      loadDeletedProducts();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleCloseRestoreDialog = () => {
    setRestoreDialog({ open: false, product: null, conflictData: null, selectedCode: '' });
  };

  // Users Functions
  const loadUsers = async () => {
    setLoadingUsers(true);
    const result = await userService.getAll();
    if (result.success) {
      setUsers(result.data.filter((user) => ['admin', 'vendedor'].includes(user.role)));
    }
    setLoadingUsers(false);
  };

  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        username: user.username,
        email: user.email,
        password: '', // No mostrar password al editar
        fullName: user.fullName,
        phone: user.phone || '',
        role: user.role,
        isActive: user.isActive
      });
    } else {
      setEditingUser(null);
      setUserFormData({
        username: '',
        email: '',
        password: '',
        fullName: '',
        phone: '',
        role: 'vendedor',
        isActive: true
      });
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setEditingUser(null);
  };

  const handleUserSubmit = async () => {
    // Validación
    if (!userFormData.username || !userFormData.email || !userFormData.fullName) {
      setMessage({ type: 'error', text: 'Completa todos los campos obligatorios' });
      return;
    }

    if (!editingUser && !userFormData.password) {
      setMessage({ type: 'error', text: 'La contraseña es obligatoria para nuevos usuarios' });
      return;
    }

    let result;
    const dataToSend = { ...userFormData };
    
    // Si estamos editando y no se cambió la contraseña, no enviarla
    if (editingUser && !dataToSend.password) {
      delete dataToSend.password;
    }

    if (editingUser) {
      result = await userService.update(editingUser.id, dataToSend);
    } else {
      result = await userService.create(dataToSend);
    }

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      handleCloseUserDialog();
      loadUsers();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('¿Estás seguro de desactivar este usuario?')) {
      const result = await userService.delete(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadUsers();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  const handleUserFormChange = (field, value) => {
    setUserFormData(prev => ({ ...prev, [field]: value }));
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrador',
      vendedor: 'Vendedor',
    };
    return roles[role] || role;
  };

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="SISTEMA"
        title="Configuración"
        subtitle="Personalizá el negocio, las listas, los usuarios y los parámetros del sistema."
        icon={<Save />}
      />

      {message.text && (
        <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} className="mb-md">
          {message.text}
        </Alert>
      )}

      <Card>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Configuración tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Información del Negocio" />
          <Tab label="Categorías" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label="Marcas" icon={<LocalOffer />} iconPosition="start" />
          <Tab label="Usuarios" icon={<People />} iconPosition="start" />
          <Tab label="Numeración de Ventas" icon={<Numbers />} iconPosition="start" />
          <Tab label="Productos Eliminados" icon={<RestoreFromTrash />} iconPosition="start" />
        </Tabs>

        {/* Tab 1: Business Info */}
        <TabPanel value={currentTab} index={0}>
          <form onSubmit={handleBusinessSubmit}>
            <CardContent>
              <Typography variant="h6" className="mb-md font-semibold">
                Información General
              </Typography>
              
              {/* Logo Section - Separado */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" className="mb-sm font-semibold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon /> Logo Principal
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={5}>
                  <Box 
                    sx={{ 
                      border: '2px dashed',
                      borderColor: logoPreview ? 'success.main' : 'grey.400',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    {logoPreview ? (
                      // Logo existente
                      <Box>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            mb: 1.5,
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1
                          }}
                        >
                          <img
                            src={logoPreview}
                            alt="Logo"
                            style={{ 
                              maxWidth: '250px', 
                              maxHeight: '100px', 
                              objectFit: 'contain',
                              width: '100%',
                              height: 'auto'
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Logo actual
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            color="success"
                            component="label"
                            size="small"
                            startIcon={<CloudUpload />}
                          >
                            Cambiar
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleLogoChange}
                            />
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteOutline />}
                            onClick={handleDeleteLogo}
                            disabled={loading}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      // Sin logo
                      <Box sx={{ py: 2 }}>
                        <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          Sin logo
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                          Haz clic para subir un logo
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          component="label"
                          startIcon={<CloudUpload />}
                        >
                          Subir Logo
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </Button>
                      </Box>
                    )}
                  </Box>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tipos permitidos: PNG, JPG, JPEG, SVG, GIF, WEBP
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tamaño máximo: 2MB
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Formulario de Información */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre del Negocio"
                    value={formData.businessName}
                    onChange={(e) => handleBusinessFormChange('businessName', e.target.value)}
                    required
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="CUIT"
                    value={formData.cuit}
                    onChange={(e) => handleBusinessFormChange('cuit', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={formData.description}
                    onChange={(e) => handleBusinessFormChange('description', e.target.value)}
                    multiline
                    rows={3}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Condición Tributaria"
                    value={formData.taxCondition}
                    onChange={(e) => handleBusinessFormChange('taxCondition', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
              </Grid>
            </CardContent>

            <CardContent>
              <Typography variant="h6" className="mb-md font-semibold">
                Ubicación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={formData.address}
                    onChange={(e) => handleBusinessFormChange('address', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    value={formData.city}
                    onChange={(e) => handleBusinessFormChange('city', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Provincia"
                    value={formData.province}
                    onChange={(e) => handleBusinessFormChange('province', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Código Postal"
                    value={formData.postalCode}
                    onChange={(e) => handleBusinessFormChange('postalCode', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
              </Grid>
            </CardContent>

            <CardContent>
              <Typography variant="h6" className="mb-md font-semibold">
                Contacto
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={formData.phone}
                    onChange={(e) => handleBusinessFormChange('phone', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleBusinessFormChange('email', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp"
                    value={formData.whatsapp}
                    onChange={(e) => handleBusinessFormChange('whatsapp', e.target.value)}
                    placeholder="Ej: 1234567890"
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sitio Web"
                    value={formData.website}
                    onChange={(e) => handleBusinessFormChange('website', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={formData.instagram}
                    onChange={(e) => handleBusinessFormChange('instagram', e.target.value)}
                    placeholder="@usuario"
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    value={formData.facebook}
                    onChange={(e) => handleBusinessFormChange('facebook', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Horarios de Atención"
                    value={formData.openingHours}
                    onChange={(e) => handleBusinessFormChange('openingHours', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Lunes a Viernes: 9:00 - 18:00&#10;Sábados: 9:00 - 13:00"
                    autoComplete="off"
                  />
                </Grid>
              </Grid>
            </CardContent>

            <CardContent>
              <Typography variant="h6" className="mb-md font-semibold">
                Respaldo de Base de Datos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Genera el backup, lo descarga automaticamente y lo elimina del servidor.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2
                }}
              >
                <Box>
                  <Typography variant="body2">
                    Tamaño de la base actual:{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {backupInfo.databaseSize}
                    </Box>
                  </Typography>
                  <Typography variant="body2">
                    Backups guardados:{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {backupInfo.backupsCount}
                    </Box>
                  </Typography>
                  <Typography variant="body2">
                    Tamaño total en backups:{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {backupInfo.backupsTotalSize}
                    </Box>
                  </Typography>
                </Box>

                <Button
                  type="button"
                  variant="contained"
                  color="warning"
                  startIcon={creatingBackup ? <CircularProgress size={18} /> : <Backup />}
                  onClick={handleCreateBackup}
                  disabled={creatingBackup}
                >
                  {creatingBackup ? 'Procesando backup...' : 'Descargar Backup Ahora'}
                </Button>
              </Box>
            </CardContent>

            <CardContent>
              <Box className="d-flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </Box>
            </CardContent>
          </form>
        </TabPanel>

        {/* Tab 2: Categories */}
        <TabPanel value={currentTab} index={1}>
          <CardContent>
            <Box className="d-flex justify-between align-center mb-md">
              <Typography variant="h6" className="font-semibold">
                Gestión de Categorías
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenCategoryDialog()}
              >
                Nueva Categoría
              </Button>
            </Box>

            {loadingCategories ? (
              <Box className="d-flex justify-center align-center py-lg">
                <CircularProgress />
              </Box>
            ) : (
              <Box className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No hay categorías registradas
                        </td>
                      </tr>
                    ) : (
                      categories.map((category) => (
                        <tr key={category.id}>
                          <td className="font-semibold">{category.name}</td>
                          <td>{category.description || '-'}</td>
                          <td className="text-center">
                            <Chip
                              label={category.isActive ? 'Activa' : 'Inactiva'}
                              color={category.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </td>
                          <td className="text-center">
                            <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteCategory(category.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Tab 3: Brands */}
        <TabPanel value={currentTab} index={2}>
          <CardContent>
            <Box className="d-flex justify-between align-center mb-md">
              <Typography variant="h6" className="font-semibold">
                Gestión de Marcas
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenBrandDialog()}
              >
                Nueva Marca
              </Button>
            </Box>

            {loadingBrands ? (
              <Box className="d-flex justify-center align-center py-lg">
                <CircularProgress />
              </Box>
            ) : (
              <Box className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brands.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No hay marcas registradas
                        </td>
                      </tr>
                    ) : (
                      brands.map((brand) => (
                        <tr key={brand.id}>
                          <td className="font-semibold">{brand.name}</td>
                          <td>{brand.description || '-'}</td>
                          <td className="text-center">
                            <Chip
                              label={brand.isActive ? 'Activa' : 'Inactiva'}
                              color={brand.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </td>
                          <td className="text-center">
                            <IconButton size="small" onClick={() => handleOpenBrandDialog(brand)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteBrand(brand.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Tab 4: Users */}
        <TabPanel value={currentTab} index={3}>
          <CardContent>
            <Box className="d-flex justify-between align-center mb-md">
              <Typography variant="h6" className="font-semibold">
                Usuarios del sistema
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenUserDialog()}
              >
                Nuevo usuario interno
              </Button>
            </Box>

            {loadingUsers ? (
              <Box className="d-flex justify-center align-center py-lg">
                <CircularProgress />
              </Box>
            ) : (
              <Box className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Nombre Completo</th>
                      <th>Email</th>
                      <th className="text-center">Rol</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No hay administradores ni vendedores registrados
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td className="font-semibold">{user.username}</td>
                          <td>{user.fullName}</td>
                          <td>{user.email}</td>
                          <td className="text-center">
                            <Chip
                              label={getRoleLabel(user.role)}
                              color={user.role === 'admin' ? 'error' : 'primary'}
                              size="small"
                            />
                          </td>
                          <td className="text-center">
                            <Chip
                              label={user.isActive ? 'Activo' : 'Inactivo'}
                              color={user.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </td>
                          <td className="text-center">
                            <IconButton size="small" onClick={() => handleOpenUserDialog(user)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Tab 5: Sale Numbering Config */}
        <TabPanel value={currentTab} index={4}>
          <SaleNumberingConfig />
        </TabPanel>

        {/* Tab 6: Deleted Products */}
        <TabPanel value={currentTab} index={5}>
          <CardContent>
            <Box className="mb-md">
              <Typography variant="h6" className="font-semibold mb-sm">
                ♻️ Productos Eliminados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Solo se pueden restaurar productos con historial de ventas (soft delete)
              </Typography>
            </Box>

            {/* Buscador */}
            <Box className="mb-md">
              <TextField
                fullWidth
                placeholder="Buscar producto eliminado..."
                value={searchDeleted}
                onChange={(e) => setSearchDeleted(e.target.value)}
                InputProps={{
                  startAdornment: <IconButton size="small"><Search /></IconButton>
                }}
              />
            </Box>

            {/* Contador */}
            <Box className="mb-md">
              <Typography variant="body2" color="text.secondary">
                {deletedProducts.length} producto{deletedProducts.length !== 1 ? 's' : ''} eliminado{deletedProducts.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {loadingDeletedProducts ? (
              <Box className="d-flex justify-center align-center py-lg">
                <CircularProgress />
              </Box>
            ) : (
              <Box className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Imagen</th>
                      <th>Código Original</th>
                      <th>Nombre</th>
                      <th>Categoría</th>
                      <th>Marca</th>
                      <th className="text-right">Precio</th>
                      <th className="text-center">Ventas</th>
                      <th className="text-center">Eliminado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedProducts.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No hay productos eliminados
                        </td>
                      </tr>
                    ) : (
                      deletedProducts.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <img
                              src={product.image ? uploadService.getImageUrl(product.image) : '/no-image.svg'}
                              alt={product.name}
                              loading="lazy"
                              className="img-thumbnail opacity-60"
                              onError={(e) => {
                                e.target.src = '/no-image.svg';
                              }}
                            />
                          </td>
                          <td>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {product.originalCode || 'Sin código'}
                            </Box>
                          </td>
                          <td>
                            <Box sx={{ color: 'text.secondary' }}>
                              {product.name}
                            </Box>
                          </td>
                          <td>{product.category?.name || '-'}</td>
                          <td>{product.brand?.name || '-'}</td>
                          <td className="text-right">
                            ${product.price.toLocaleString('es-AR')}
                          </td>
                          <td className="text-center">
                            <Chip
                              label={product.salesCount || 0}
                              size="small"
                              color={product.salesCount > 0 ? 'primary' : 'default'}
                              icon={<ShoppingCart />}
                            />
                          </td>
                          <td className="text-center">
                            <Typography variant="caption" color="text.secondary">
                              {product.deletedAt 
                                ? new Date(product.deletedAt).toLocaleDateString('es-AR')
                                : '-'
                              }
                            </Typography>
                          </td>
                          <td className="text-center">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleRestore(product)}
                              title="Restaurar producto"
                            >
                              <RestoreFromTrash fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} className="mt-xs">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={categoryFormData.name}
                onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={categoryFormData.description}
                onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                multiline
                rows={3}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>Cancelar</Button>
          <Button onClick={handleCategorySubmit} variant="contained">
            {editingCategory ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={openBrandDialog} onClose={handleCloseBrandDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBrand ? 'Editar Marca' : 'Nueva Marca'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} className="mt-xs">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={brandFormData.name}
                onChange={(e) => handleBrandFormChange('name', e.target.value)}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={brandFormData.description}
                onChange={(e) => handleBrandFormChange('description', e.target.value)}
                multiline
                rows={3}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBrandDialog}>Cancelar</Button>
          <Button onClick={handleBrandSubmit} variant="contained">
            {editingBrand ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleUserSubmit();
          }}
        >
        <DialogContent>
          <Grid container spacing={2} className="mt-xs">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Usuario"
                value={userFormData.username}
                onChange={(e) => handleUserFormChange('username', e.target.value)}
                required
                autoComplete="off"
                helperText="Nombre de usuario para iniciar sesión"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={userFormData.fullName}
                onChange={(e) => handleUserFormChange('fullName', e.target.value)}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userFormData.email}
                onChange={(e) => handleUserFormChange('email', e.target.value)}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Teléfono"
                type="tel"
                value={userFormData.phone}
                onChange={(e) => handleUserFormChange('phone', e.target.value)}
                autoComplete="off"
                helperText="Opcional: número de contacto"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editingUser ? "Nueva Contraseña (dejar vacío para no cambiar)" : "Contraseña"}
                type="password"
                value={userFormData.password}
                onChange={(e) => handleUserFormChange('password', e.target.value)}
                required={!editingUser}
                autoComplete="new-password"
                helperText={editingUser ? "Deja este campo vacío si no deseas cambiar la contraseña" : "Mínimo 6 caracteres"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Rol"
                value={userFormData.role}
                onChange={(e) => handleUserFormChange('role', e.target.value)}
                required
              >
                <MenuItem value="admin">Administrador - Acceso Total</MenuItem>
                <MenuItem value="vendedor">Vendedor - POS y Ventas</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box className="d-flex align-center">
                <TextField
                  fullWidth
                  select
                  label="Estado"
                  value={userFormData.isActive}
                  onChange={(e) => handleUserFormChange('isActive', e.target.value === 'true')}
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </TextField>
                <Tooltip title={userFormData.isActive ? "El usuario puede iniciar sesión" : "El usuario NO puede iniciar sesión"}>
                  <IconButton size="small" color="info">
                    <Search />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {editingUser ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
        </Box>
      </Dialog>

      {/* Restore Product Dialog */}
      <Dialog 
        open={restoreDialog.open} 
        onClose={handleCloseRestoreDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          ⚠️ Conflicto de Código
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {restoreDialog.conflictData?.sugerencias && (
              <>
                El código original ya está en uso por otro producto.
                <br />
                Selecciona un código alternativo para restaurar:
              </>
            )}
          </Alert>

          {restoreDialog.conflictData?.sugerencias && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sugerencias:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {restoreDialog.conflictData.sugerencias.map((sugerencia, index) => (
                  <Chip
                    key={index}
                    label={sugerencia}
                    color={restoreDialog.selectedCode === sugerencia ? 'primary' : 'default'}
                    onClick={() => setRestoreDialog(prev => ({ 
                      ...prev, 
                      selectedCode: sugerencia 
                    }))}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            label="Código para restaurar"
            value={restoreDialog.selectedCode}
            onChange={(e) => setRestoreDialog(prev => ({ 
              ...prev, 
              selectedCode: e.target.value 
            }))}
            helperText="Puedes modificar el código o usar una de las sugerencias"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRestoreDialog}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRestoreWithNewCode} 
            variant="contained"
            color="success"
            startIcon={<RestoreFromTrash />}
          >
            Restaurar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
